import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { axiosErrorHandler } from 'common/helpers/axios.helper';
import { AppDispatch, RootState, store } from 'app/store';
import { ESelfRegistrationStep, IUpdateProfilePayload } from 'entities/SelfRegistration/SelfRegistration.models';
import { selfRegistrationTransport } from 'entities/SelfRegistration/SelfRegistration.transport';
import { saveCreds } from 'entities/Auth/Auth.helper';
import { authTransport } from 'entities/Auth/Auth.transport';

interface IState {
  selfRegistrationStep: ESelfRegistrationStep | null;
  isSelfRegistrationLoading: boolean;
  selfRegistrationError: string | null;
}

const initialState: IState = {
  selfRegistrationStep: null,
  isSelfRegistrationLoading: false,
  selfRegistrationError: null,
};

export const selfRegistrationSlice = createSlice({
  name: 'selfRegistration',
  initialState,
  reducers: {
    setSelfRegistrationStep(state, action: PayloadAction<ESelfRegistrationStep | null>) {
      state.selfRegistrationStep = action.payload;
    },
    setIsSelfRegistrationLoading(state, action: PayloadAction<boolean>) {
      state.isSelfRegistrationLoading = action.payload;
    },
    setSelfRegistrationError(state, action: PayloadAction<string | null>) {
      state.selfRegistrationError = action.payload;
    },
  },
});

export const { setSelfRegistrationStep, setIsSelfRegistrationLoading, setSelfRegistrationError } = selfRegistrationSlice.actions;

export const selectSelfRegistrationStep = (state: RootState) => state.selfRegistration.selfRegistrationStep;
export const selectIsSelfRegistrationLoading = (state: RootState) => state.selfRegistration.isSelfRegistrationLoading;
export const selectSelfRegistrationError = (state: RootState) => state.selfRegistration.selfRegistrationError;

export const selfRegistrationReducer = selfRegistrationSlice.reducer;

export const getSetSelfRegistrationStep = () => async (dispatch: AppDispatch) => {
  dispatch(setIsSelfRegistrationLoading(true));

  try {
    const response = await selfRegistrationTransport.getStep();
    dispatch(setSelfRegistrationStep((response.step as ESelfRegistrationStep) ?? null));
  } catch (error) {
    axiosErrorHandler(error, setSelfRegistrationError);
  } finally {
    dispatch(setIsSelfRegistrationLoading(false));
  }
};

export const updateProfile = (payload: IUpdateProfilePayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsSelfRegistrationLoading(true));

  try {
    const response = await selfRegistrationTransport.updateProfile(payload);
    dispatch(setSelfRegistrationStep((response.step as ESelfRegistrationStep) ?? null));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error, setSelfRegistrationError);
  } finally {
    dispatch(setIsSelfRegistrationLoading(false));
  }
};

export const completeLegals = () => async (dispatch: AppDispatch) => {
  dispatch(setIsSelfRegistrationLoading(true));

  try {
    const response = await selfRegistrationTransport.completeLegals();
    dispatch(setSelfRegistrationStep((response.step as ESelfRegistrationStep) ?? null));
  } catch (error) {
    axiosErrorHandler(error, setSelfRegistrationError);
  } finally {
    dispatch(setIsSelfRegistrationLoading(false));
  }
};

export const completeAddresses = () => async (dispatch: AppDispatch) => {
  dispatch(setIsSelfRegistrationLoading(true));

  try {
    const response = await selfRegistrationTransport.completeAddresses();
    dispatch(setSelfRegistrationStep((response.step as ESelfRegistrationStep) ?? null));

    const state = store.getState();
    const auth = state.auth.auth;

    if (auth?.refresh.token) {
      const refreshResponse = await authTransport.refreshToken(auth.refresh.token);
      await saveCreds(refreshResponse);
    }
  } catch (error) {
    axiosErrorHandler(error, setSelfRegistrationError);
  } finally {
    dispatch(setIsSelfRegistrationLoading(false));
  }
};

export const getSetSelfRegistrationPreviousStep = () => async (dispatch: AppDispatch) => {
  dispatch(setIsSelfRegistrationLoading(true));

  try {
    const response = await selfRegistrationTransport.getPreviousStep();
    dispatch(setSelfRegistrationStep((response.step as ESelfRegistrationStep) ?? null));
  } catch (error) {
    axiosErrorHandler(error, setSelfRegistrationError);
  } finally {
    dispatch(setIsSelfRegistrationLoading(false));
  }
};
